export type CollectionObjectType = 'collection' | 'parent' | 'gallery'

export type CollectionObjectRef = {
	type: CollectionObjectType
	id?: string
	slug: string
	apiPath: string
}

const OBJECT_PREFIXES: Record<Exclude<CollectionObjectType, 'collection'>, string> = {
	parent: 'parent:',
	gallery: 'gallery:'
}

const OBJECT_LABELS: Record<Exclude<CollectionObjectType, 'collection'>, string> = {
	parent: 'Parent Collection',
	gallery: 'On-chain Gallery'
}

/** `decodeURIComponent` that tolerates a stray `%` (a slug like `brc20-25%`) instead of throwing `URIError: URI malformed`. */
export function safeDecodeCollectionSlug(slug?: string | null) {
	if (typeof slug !== 'string') return ''
	try {
		return decodeURIComponent(slug)
	} catch (_e) {
		return slug
	}
}

export function parseCollectionObjectSlug(slug?: string | null): CollectionObjectRef {
	const raw = safeDecodeCollectionSlug(slug)

	for (const type of Object.keys(OBJECT_PREFIXES) as Array<
		Exclude<CollectionObjectType, 'collection'>
	>) {
		const prefix = OBJECT_PREFIXES[type]
		if (raw.startsWith(prefix)) {
			const id = raw.slice(prefix.length)
			return {
				type,
				id,
				slug: `${prefix}${id}`,
				apiPath: `${type}/${encodeURIComponent(id)}`
			}
		}
	}

	return {
		type: 'collection',
		slug: raw,
		apiPath: `collection/${encodeURIComponent(raw)}`
	}
}

export function isCollectionObjectSlug(slug?: string | null) {
	return parseCollectionObjectSlug(slug).type !== 'collection'
}

export function collectionObjectApiPath(slug?: string | null) {
	return parseCollectionObjectSlug(slug).apiPath
}

export function collectionObjectRouteSlug(
	type: Exclude<CollectionObjectType, 'collection'>,
	id: string
) {
	return `${OBJECT_PREFIXES[type]}${id}`
}

export function collectionObjectPagePath(slug?: string | null) {
	const ref = parseCollectionObjectSlug(slug)
	if (ref.type !== 'collection' && ref.id) {
		return `/inscription/${encodeURIComponent(ref.id)}`
	}

	return `/collection/${encodeURIComponent(ref.slug)}`
}

function objectName(
	type: Exclude<CollectionObjectType, 'collection'>,
	id?: string,
	name?: string | null
) {
	if (name) return name
	if (!id) return OBJECT_LABELS[type]
	return `${OBJECT_LABELS[type]} ${id.slice(0, 8)}...${id.slice(-4)}`
}

export function normalizeCollectionObjectMeta(meta: any, slug?: string | null) {
	const ref = parseCollectionObjectSlug(slug)
	if (ref.type === 'collection') return meta

	const objectType = ref.type
	const id =
		ref.id ||
		meta?.parent_inscription_id ||
		meta?.gallery_inscription_id ||
		meta?.icon_inscription?.id ||
		''
	const totalSupply = meta?.total_supply ?? meta?.child_count ?? meta?.item_count ?? 0
	const iconInscription = meta?.icon_inscription || {}
	const iconContentType =
		iconInscription?.effective_content_type ||
		meta?.effective_content_type ||
		iconInscription?.content_type ||
		meta?.content_type ||
		'image/png'

	return {
		...meta,
		id: ref.slug,
		slug: ref.slug,
		type: objectType,
		collection_object_type: objectType,
		name: objectName(objectType, id, meta?.name),
		description: meta?.description || '',
		active: true,
		total_supply: totalSupply,
		socials: meta?.socials || {},
		icon: meta?.icon || '',
		icon_inscription: {
			...iconInscription,
			id: iconInscription?.id || id,
			content_type: iconContentType
		},
		// A gallery is not "verified" just by existing — anyone can inscribe one.
		// Only surface the badge when the API explicitly marks it verified.
		verified: meta?.verified ?? false,
		parent_inscription_id: objectType === 'parent' ? id : meta?.parent_inscription_id,
		gallery_inscription_id: objectType === 'gallery' ? id : meta?.gallery_inscription_id,
		lowest_inscription_num: meta?.lowest_inscription_num ?? 0,
		highest_inscription_num: meta?.highest_inscription_num ?? 0
	}
}

export function normalizeCollectionObjectStats(stats: any, slug?: string | null) {
	const ref = parseCollectionObjectSlug(slug)
	if (ref.type === 'collection') return stats

	return {
		...stats,
		listed: stats?.listed ?? stats?.listed_count ?? 0,
		sales: stats?.sales ?? stats?.sold_all_time ?? 0,
		volume_day: stats?.volume_day ?? 0,
		volume_week: stats?.volume_week ?? 0,
		volume_all_time: stats?.volume_all_time ?? stats?.volume_total ?? 0,
		volume_total: stats?.volume_total ?? stats?.volume_all_time ?? 0,
		floor_price: stats?.floor_price ?? 0
	}
}

export function getCollectionObjectIds(items: any[] | undefined | null) {
	if (!Array.isArray(items)) return []
	return items.flatMap((item: any) => {
		const id = item?.id || item?.inscription_id
		return typeof id === 'string' && id ? [id] : []
	})
}
